<script>
  import { onMount } from "svelte";
  import { useNavigate, Link } from "svelte-navigator";
  import {
    Modal as DangerModal,
    Select,
    SelectItem,
  } from "carbon-components-svelte";
  import { ROUTES } from "@utils/routes.js";
  import Header from "@components/Header.svelte";
  import { InfoBlock, TextInput, SectionWrapper } from "paxray-components-svelte"
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import Modal from "@components/Modal.svelte";
  import RemoveIcon from "../settings/svg/RemoveIcon.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  let anonymizationPresets = [];
  let limitationPresets = [];
  let deleteAnonymizationPresetId = 0;
  let deleteLimitationPresetId = 0;
  let createAnonymizationPresetModalOpen = false;
  let deleteAnonymizationPresetModalOpen = false;
  let createLimitationPresetModalOpen = false;
  let deleteLimitationPresetModalOpen = false;
  let newAnonymizationPresetName = "";
  let newAnonymizationPresetDescription = "";
  let newLimitationPresetName = "";
  let newLimitationPresetDescription = "";
  let newLimitationPresetType = "Whitelist";
  let deleteLimitationPresetName = "";
  let searchLimitationValue = "";
  let searchRuleValue = "";
  let limitationPage = 1;
  let limitationPageSize = 10;
  let anonymizationPage = 1;
  let anonymizationPageSize = 10;
  const navigate = useNavigate();

  $: filteredLimitationPresets = limitationPresets.filter((preset) =>
    preset.name.toLowerCase().includes(searchLimitationValue.toLowerCase()),
  );

  $: filteredAnonymizationPresets = anonymizationPresets.filter((preset) =>
    preset.name.toLowerCase().includes(searchRuleValue.toLowerCase()),
  );

  const limitationActions = {
    menu: [
      {
        icon: RemoveIcon,
        label: "Delete",
        danger: true,
        onClick: (row) => {
          deleteLimitationPresetId = row.id;
          deleteLimitationPresetName = row.name;
          deleteLimitationPresetModalOpen = true;
        },
      },
    ],
  };

  const anonymizationActions = {
    menu: [
      {
        icon: RemoveIcon,
        label: "Delete",
        danger: true,
        onClick: (row) => {
          deleteAnonymizationPresetId = row.id;
          deleteAnonymizationPresetModalOpen = true;
        },
      },
    ],
  };

  function createAnonymizationPreset() {
    fetchWithSession("/api/damprojects/createAnonymizationPreset", {
      method: "POST",
      body: JSON.stringify({
        name: newAnonymizationPresetName,
        description: newAnonymizationPresetDescription,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success && data.id) {
          createAnonymizationPresetModalOpen = false;
          navigate(
            `${ROUTES.dam.presets.anonymization}?idAnonymization=${data.id}`,
          );
        } else {
          console.log(data.error);
        }
        createAnonymizationPresetModalOpen = false;
      });
  }

  function createLimitationPreset() {
    fetchWithSession("/api/damprojects/limitation-presets", {
      method: "POST",
      body: JSON.stringify({
        name: newLimitationPresetName,
        description: newLimitationPresetDescription,
        type: newLimitationPresetType,
      }),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.ok) {
          const data = res.data;
          createLimitationPresetModalOpen = false;
          navigate(`${ROUTES.dam.presets.limitation}?idLimitation=${data.id}`);
        }
      });
  }

  function confirmDeleteAnonymizationPreset() {
    fetchWithSession(
      `/api/damprojects/deleteAnonymizationPreset/${deleteAnonymizationPresetId}`,
      {
        method: "POST",
      },
    )
      .then((res) => res.json())
      .then((res) => {
        if (res.success) {
          anonymizationPresets = anonymizationPresets.filter(
            (x) => x.id != deleteAnonymizationPresetId,
          );
          deleteAnonymizationPresetModalOpen = false;
        }
      });
  }

  function confirmDeleteLimitationPreset() {
    fetchWithSession(
      `/api/damprojects/limitation-presets/${deleteLimitationPresetId}`,
      {
        method: "DELETE",
      },
    )
      .then((res) => res.json())
      .then((res) => {
        deleteLimitationPresetModalOpen = false;
        if (res.ok) {
          limitationPresets = limitationPresets.filter(
            (x) => x.id != deleteLimitationPresetId,
          );
        }
      });
  }

  onMount(() => {
    fetchWithSession(`/api/damprojects/DAMAnonymizationPresets`)
      .then((response) => response.json())
      .then((data) => {
        anonymizationPresets = data;
      });

    fetchWithSession(`/api/damprojects/limitation-presets`)
      .then((response) => response.json())
      .then((data) => {
        limitationPresets = data;
      });
  });
</script>

<Header
  breadcrumb="Presets"
  tooltipText="Reusable whitelists, blacklists and anonymization rule sets that projects can activate during setup."
  hideProjectName
/>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main">
    <InfoBlock
      type="info"
      heading="DAM Limitation Presets"
      message="Create and edit preset whitelists and blacklists."
      showClose={false}
    />
    <DataTableBlack
      headers={[
        { key: "name", value: "Name" },
        { key: "type", value: "Type" },
        { key: "menu", value: "Actions" },
      ]}
      rows={filteredLimitationPresets}
      totalItems={filteredLimitationPresets.length}
      bind:page={limitationPage}
      bind:pageSize={limitationPageSize}
      pageSizes={[10, 25, 50]}
      searchValue={searchLimitationValue}
      slicePage
      sortable
      newButtonText="New Black/Whitelist"
      newButtonIconLeft={false}
      columnActions={limitationActions}
      on:search={(e) => {
        searchLimitationValue = e.detail.value;
        limitationPage = 1;
      }}
      on:clearSearch={() => {
        searchLimitationValue = "";
        limitationPage = 1;
      }}
      on:addNew={() => {
        createLimitationPresetModalOpen = true;
      }}
    >
      <svelte:fragment slot="cell" let:row let:cell>
        {#if cell.key === "name"}
          <Link to={`${ROUTES.dam.presets.limitation}?idLimitation=${row.id}`}
            >{cell.value}</Link
          >
        {:else}
          {cell.value || ""}
        {/if}
      </svelte:fragment>
    </DataTableBlack>

    <InfoBlock
      type="info"
      heading="Anonymization Rule Presets"
      message="Create and edit preset anonymization rules."
      showClose={false}
    />
    <DataTableBlack
      headers={[
        { key: "name", value: "Name" },
        { key: "menu", value: "Actions" },
      ]}
      rows={filteredAnonymizationPresets}
      totalItems={filteredAnonymizationPresets.length}
      bind:page={anonymizationPage}
      bind:pageSize={anonymizationPageSize}
      pageSizes={[10, 25, 50]}
      searchValue={searchRuleValue}
      slicePage
      sortable
      newButtonText="New Anonymization Ruleset"
      newButtonIconLeft={false}
      columnActions={anonymizationActions}
      on:search={(e) => {
        searchRuleValue = e.detail.value;
        anonymizationPage = 1;
      }}
      on:clearSearch={() => {
        searchRuleValue = "";
        anonymizationPage = 1;
      }}
      on:addNew={() => {
        createAnonymizationPresetModalOpen = true;
      }}
    >
      <svelte:fragment slot="cell" let:row let:cell>
        {#if cell.key === "name"}
          <Link
            to={`${ROUTES.dam.presets.anonymization}?idAnonymization=${row.id}`}
            >{cell.value}</Link
          >
        {:else}
          {cell.value || ""}
        {/if}
      </svelte:fragment>
    </DataTableBlack>
  </div>
</SectionWrapper>

<Modal
  open={createAnonymizationPresetModalOpen}
  preventCloseOnClickOutside
  modalHeading="New Anonymization Preset"
  primaryButtonText="Create"
  secondaryButtonText="Cancel"
  autowidth
  actions
  primaryButtonAction={createAnonymizationPreset}
  secondaryButtonAction={() => {
    createAnonymizationPresetModalOpen = false;
  }}
  on:close={() => {
    createAnonymizationPresetModalOpen = false;
  }}
>
  <TextInput labelText="Name" bind:value={newAnonymizationPresetName} />
  <TextInput
    labelText="Description"
    bind:value={newAnonymizationPresetDescription}
  />
</Modal>

<Modal
  open={createLimitationPresetModalOpen}
  preventCloseOnClickOutside
  modalHeading="New Limitation Preset"
  primaryButtonText="Create"
  secondaryButtonText="Cancel"
  autowidth
  actions
  primaryButtonAction={createLimitationPreset}
  secondaryButtonAction={() => {
    createLimitationPresetModalOpen = false;
  }}
  on:close={() => {
    createLimitationPresetModalOpen = false;
  }}
>
  <TextInput labelText="Name" bind:value={newLimitationPresetName} />
  <TextInput
    labelText="Description"
    bind:value={newLimitationPresetDescription}
  />
  <Select labelText="Type" bind:selected={newLimitationPresetType}>
    <SelectItem value="Whitelist" text="Whitelist" />
    <SelectItem value="Blacklist" text="Blacklist" />
  </Select>
</Modal>

<DangerModal
  danger
  bind:open={deleteAnonymizationPresetModalOpen}
  preventCloseOnClickOutside
  modalHeading="Delete Preset?"
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={confirmDeleteAnonymizationPreset}
  on:click:button--secondary={() => {
    deleteAnonymizationPresetModalOpen = false;
  }}
  on:close={() => {
    deleteAnonymizationPresetModalOpen = false;
  }}
>
  <div class="deleteModalBody">
    <p>Are you sure you want to delete this Anonymization Preset?</p>
    <p>All associated rules will be deleted as well.</p>
    <p><span class="danger-text">This action cannot be undone.</span></p>
  </div>
</DangerModal>

<DangerModal
  danger
  bind:open={deleteLimitationPresetModalOpen}
  preventCloseOnClickOutside
  modalHeading="Delete Preset?"
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={confirmDeleteLimitationPreset}
  on:click:button--secondary={() => {
    deleteLimitationPresetModalOpen = false;
  }}
  on:close={() => {
    deleteLimitationPresetModalOpen = false;
  }}
>
  <div class="deleteModalBody">
    <p>
      Are you sure you want to delete this Limitation Preset:
      <span class="limitation-name">{deleteLimitationPresetName}</span>?
    </p>
    <p>All associated rules will be deleted as well.</p>
    <p><span class="danger-text">This action cannot be undone.</span></p>
  </div>
</DangerModal>

<style>
  .main {
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .deleteModalBody span {
    font-weight: bold;
  }

  .deleteModalBody span.danger-text {
    color: var(--pax-orange);
  }
</style>
